import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import moment from 'moment';
import Can from 'components/Can';
import { buildUrl } from 'utils';
import { debounce } from 'lodash';
import Title from 'components/Title';
import { Table } from 'components/UI';
import { UserContext } from 'context';
import Layout from 'components/Layout';
import { FETCH_STATE } from 'constants';
import Feature from 'components/Feature';
import { ability } from 'config/ability';
import { uiActions } from 'state/modules/UI';
import { useConfirm, useQuery } from 'hooks';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import { useHistory } from 'react-router-dom';
import OrgAbility from 'components/OrgAbility';
import { RefundModal } from 'components/Modals';
import SearchIcon from '@mui/icons-material/Search';
import { formatAmount, toQueryString } from 'utils';
import { SearchField } from 'components/FormFields';
import { UsersSelect } from 'components/AsyncFields';
import { InvoiceActions } from 'state/modules/invoice';
import { PartnersSelect, AccountStatementSelect } from 'components/AsyncFields';
import { DatetimeField } from 'components/DatetimeField';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import { InvoiceStatusesSelect, InvoiceTypesSelect } from 'components/DataFields';

// Replaces the v4 <Hidden only="xs"> wrapper
const hiddenOnXsSx = { display: { xs: 'none', sm: 'block' } };

const InvoiceList = () => {
  const history = useHistory();
  const urlParams = useQuery();
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });

  const { orgType } = useContext(UserContext);
  const [selectedInvoices, setSelectedInvoice] = useState([]);
  const [openRefundModal, setOpenRefundModal] = useState(false);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);

  const core = useSelector((state) => state.config?.settings?.core || {});
  const user = useSelector((state) => state.auth.user || {}) || {};
  const currentOrganization = useSelector((state) => state.auth?.currentOrganization);
  const data = useSelector((state) => state.invoice.invoices);
  const pagination = useSelector((state) => state.invoice.pagination);
  const fetchingInvoices = useSelector((state) => state.invoice.fetchingInvoices);
  const fetchingProcessingRefunds = useSelector((state) => state.invoice.fetchingProcessingRefunds);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const invoice = useSelector((state) => state.invoice.invoice);
  const { localeFormat } = useSelector((state) => state.organizations);

  // TO CONTEXT
  const hasPartnership = core?.partnership?.enabled || false;
  const hasBilling = core?.partnership?.billing || false;
  const sharingTypeFAAS = sharingType === 'FAAS';
  const sharingTypeFFB = sharingType === 'FFB';
  const isOwner = orgType === 'msOwnerOrg';
  const isSuperAdmin = user?.role === 'superadmin';

  // TO CONTEXT AS WELL
  const isFFBEnabled = sharingTypeFFB && hasPartnership;
  const isFAASEnabled = sharingTypeFAAS && hasPartnership;
  const showMarkAsPaidButton = isOwner && !isFFBEnabled;
  const showProcessButton = !sharingTypeFFB;
  const showRefundButton = sharingTypeFAAS && isSuperAdmin;
  const showCloneButton = isOwner;
  const showCreateButton = isOwner;
  const showAccountSelect = isFFBEnabled;
  const showSelectCheckBox =
    showMarkAsPaidButton ||
    showProcessButton ||
    showRefundButton ||
    showCloneButton ||
    showCreateButton;
  const showPartnerSelectFilter =
    ((isFAASEnabled || isFFBEnabled) && isOwner) ||
    currentOrganization?.organization_type !== 'BUSINESS';
  const showLatePaymentFilter = !isFFBEnabled;

  // WILL BE KEEP AT THE COMPONENT LEVEL
  const refetch =
    fetchingProcessingRefunds === FETCH_STATE.DONE && fetchingInvoices !== FETCH_STATE.LOADING;
  const listTitle = hasBilling
    ? sharingTypeFFB
      ? i18n.t('statement_entries.list_title')
      : i18n.t('invoice.title')
    : i18n.t('invoice.invoices');
  const moneyFlow = sharingTypeFFB
    ? i18n.t('statement_entries.money_flow')
    : i18n.t('invoice.payment_flow');
  const flowOut = sharingTypeFFB
    ? i18n.t('statement_entries.money_out')
    : i18n.t('invoice.payments_out');
  const flowIn = sharingTypeFFB
    ? i18n.t('statement_entries.money_in')
    : i18n.t('invoice.payments_in');

  const markAsPaid = () => {
    dispatch(
      InvoiceActions.fetchMarkInvoicesAsPaidByIds(selectedInvoices.map((invoice) => invoice.id))
    );
  };

  const [toggleMarkAsPaid, MarkAsPaid] = useConfirm({
    message: i18n.t('invoice.confirm_mark_as_paid'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.yes'),
    onConfirm: markAsPaid,
  });

  const handleMarkAsPaid = () => {
    const hasDraftInvoices = selectedInvoices.filter((e) => e.status === 'DRAFT') || false;

    if (hasDraftInvoices?.length > 0) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          autoHideDuration: null,
          message: i18n.t('invoice.process_draft_invoices_msg'),
        })
      );
    }

    toggleMarkAsPaid();
  };

  const handleModalOpen = (type) => {
    if (type === 'success') {
      setSelectedInvoice([]);
      setClearSelectedRows((prevState) => !prevState);
      fetchInvoices(params);
    }
    dispatch(InvoiceActions.resetRefund());
    setOpenRefundModal((prevState) => !prevState);
  };

  const onCreateInvoice = () => {
    dispatch(InvoiceActions.resetWizard());
    history.push(buildUrl(sharingTypeFFB ? '/statement-entries/create' : '/invoices/create'));
  };

  const cloneInvoice = () => {
    const id = selectedInvoices?.[0]?.id;
    dispatch(InvoiceActions.resetWizard());
    history.push(
      buildUrl(sharingTypeFFB ? `/statement-entries/${id}/clone` : `/invoices/${id}/clone`)
    );
  };

  const handleParams = (params) => {
    const data = { ...params };
    setParams(data);
    const _params = toQueryString(data);
    const url = isFFBEnabled ? `/statement-entries${_params}` : `/invoices${_params}`;
    history.replace(buildUrl(url));
  };

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  const handleRowClick = (row) => {
    const isBill = row?.account_customer?.holder_id === currentOrganization?.id;
    let url;

    if (isBill) {
      url = sharingTypeFFB ? `/statement-entries/${row?.id}/preview` : `/bills/${row?.id}`;
    } else {
      url = sharingTypeFFB ? `/statement-entries/${row?.id}` : `/invoices/${row?.id}`;
    }
    history.push(buildUrl(url));
  };

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  const handleSearch = (name, value, res, extra = {}) => {
    const searchParams = {
      ...params,
      page: 1,
      ...extra,
      [name]: name === 'account_customer_id' ? res?.account_id : value,
    };

    switch (name) {
      case 'account_customer_id':
        delete searchParams.account_emitter_id;
        delete searchParams.type;
        break;
      case 'account_emitter_id':
        delete searchParams.account_customer_id;
        break;
      default:
        break;
    }

    Object.keys(searchParams).forEach(
      (key) => !searchParams[key] && searchParams[key] !== false && delete searchParams[key]
    );

    handleParams({ ...searchParams });
  };

  const fetchInvoices = useCallback(
    (params = {}) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(InvoiceActions.fetchInvoices(params, { signal: abortController?.current?.signal }));
    },
    [dispatch]
  );

  const fetchInvoicesDebounced = useCallback(
    debounce((params) => fetchInvoices(params), 500),
    [fetchInvoices]
  );

  /**
   * Handle selected row
   * @param {object} row Data of the row selected
   */
  const handleSelected = (state) => {
    setSelectedInvoice(
      state.selectedRows.map((row) => {
        return {
          id: row.id,
          status: row.status,
          invoiced: row.invoiced,
          internal: row.internal,
        };
      })
    );
  };

  const processInvoiceBalance = () => {
    const hasDraftInvoices = selectedInvoices.filter((e) => e.status === 'DRAFT') || false;

    if (hasDraftInvoices?.length > 0) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          autoHideDuration: null,
          message: i18n.t('invoice.process_draft_invoices_msg'),
        })
      );
    }
    dispatch(
      InvoiceActions.fetchProcessInvoicesByIds(selectedInvoices.map((invoice) => invoice.id))
    );
  };

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchInvoicesDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchInvoicesDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchInvoices(params);
  }, []);

  // On refetch delete/update success no debounce
  useEffect(() => {
    if (refetch) {
      setSelectedInvoice([]);
      setClearSelectedRows((prevState) => !prevState);
      fetchInvoices(params);
    }
  }, [fetchInvoices, refetch]);

  const columns = [
    { name: i18n.t('common.id'), selector: 'id', sortable: true, grow: 0 },
    {
      name:
        currentOrganization?.organization_type === 'SERVICE' && hasBilling
          ? i18n.t('invoice.customer_and_partners')
          : currentOrganization?.organization_type === 'SERVICE' &&
              currentOrganization?.organization_type !== 'BUSINESS' &&
              !hasBilling
            ? i18n.t('invoice.customer')
            : i18n.t('common.partner'),
      selector: 'customer_name',
      cell: (row) =>
        row.account_customer.holder_id === currentOrganization?.id
          ? row?.account_emitter?.customer_name
          : row?.account_customer?.customer_name,
    },
    {
      name: moneyFlow,
      selector: 'in_and_out',
      cell: (row) =>
        row.account_customer.holder_id === currentOrganization?.id ? flowOut : flowIn,
      grow: 1,
      omit: !hasBilling,
    },
    {
      name: i18n.t('invoice.reference'),
      selector: 'source_name',
      format: (row) => {
        const { source, source_id } =
          row?.source_name === 'SubscriptionPeriod'
            ? {
                source: row?.source_name,
                source_id: row?.extra_source_id,
              }
            : { source: row?.source_name, source_id: row?.source_id };
        //TODO: Use BE once full source name is provided
        if (source === 'OwnerVehicleGroup' && row.description && hasPartnership) {
          return row.description?.replace('VehicleGroup', 'Fleet');
        }
        return `${i18n.t(`invoice.source_type.${source}`, {
          defaultValue: source,
        })} ID#${source_id}`;
      },
      sortable: true,
      grow: 1,
    },
    {
      name:
        hasPartnership && sharingType === 'FFB'
          ? i18n.t('invoice.due_date')
          : i18n.t('invoice.due_datetime'),
      selector: 'due_time',
      cell: (row) =>
        row.due_time ? (
          hasPartnership && sharingType === 'FFB' ? (
            moment(invoice?.due_time).utc(false).format(localeFormat)
          ) : (
            <DatetimeField datetime={row.due_time} />
          )
        ) : (
          '--'
        ),
      sortable: true,
      grow: 1,
    },
    {
      name: i18n.t('invoice.total'),
      selector: 'invoiced',
      format: (row) => `$ ${formatAmount(row.invoiced)}`,
      sortable: true,
      grow: 0,
    },
    {
      name: i18n.t('common.status'),
      selector: 'status',
      format: (row) => i18n.t(`invoice_statuses.${row.status}`, { defaultValue: row.status }),
      grow: 0,
      minWidth: '8rem',
    },
  ];

  return (
    <Layout title={listTitle}>
      <Container sx={{ minWidth: '100%', margin: 0 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid size={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid style={{ flexGrow: 1 }}>
                <Title title={listTitle} />
              </Grid>
              {showMarkAsPaidButton ? (
                <Button
                  variant="outlined"
                  onClick={handleMarkAsPaid}
                  style={{
                    textTransform: 'capitalize',
                  }}
                  color="primary"
                  disabled={!selectedInvoices.length}
                >
                  {i18n.t('invoice.mark_as_paid')}
                </Button>
              ) : null}
              {showProcessButton && (
                <Can I="process" a="receivable">
                  <Grid>
                    <Box sx={hiddenOnXsSx}>
                      <Button
                        onClick={processInvoiceBalance}
                        variant="outlined"
                        style={{
                          textTransform: 'capitalize',
                        }}
                        color="primary"
                        disabled={
                          !selectedInvoices.length || fetchingInvoices === FETCH_STATE.LOADING
                        }
                      >
                        {i18n.t('invoice.process_balance')}
                      </Button>
                    </Box>
                  </Grid>
                </Can>
              )}
              {showRefundButton ? (
                <Can I="cancel" a="receivable">
                  <Grid>
                    <Box sx={hiddenOnXsSx}>
                      <Button
                        onClick={handleModalOpen}
                        variant="outlined"
                        style={{
                          textTransform: 'capitalize',
                        }}
                        color="primary"
                        disabled={!selectedInvoices.length}
                      >
                        {i18n.t('invoice.refund')}
                      </Button>
                    </Box>
                  </Grid>
                </Can>
              ) : null}
              {showCloneButton ? (
                <Can I="create" a="invoice">
                  <Grid>
                    <Box sx={hiddenOnXsSx}>
                      <Button
                        color="primary"
                        variant="outlined"
                        onClick={cloneInvoice}
                        style={{
                          textTransform: 'capitalize',
                        }}
                        disabled={
                          selectedInvoices.length !== 1 ||
                          selectedInvoices.every((invoice) => !invoice.internal)
                        }
                      >
                        {i18n.t('common.clone')}
                      </Button>
                    </Box>
                  </Grid>
                </Can>
              ) : null}
              {showCreateButton ? (
                <Can I="create" a="invoice">
                  <Grid>
                    <Box sx={hiddenOnXsSx}>
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={onCreateInvoice}
                        style={{
                          textTransform: 'capitalize',
                        }}
                      >
                        {i18n.t('invoice.create')}
                      </Button>
                    </Box>
                  </Grid>
                </Can>
              ) : null}
            </Grid>
          </Grid>
          {core?.subscription?.enabled && (
            <Grid size={{ xs: 6, sm: 2 }}>
              <SearchField
                margin="none"
                label={i18n.t('invoice.search_by_subs')}
                field={{
                  onChange: (name, value) => {
                    handleSearch(name, value, null, {
                      source_name: value ? 'Subscription' : undefined,
                    });
                  },
                  name: 'source_id',
                  value: params.source_id,
                }}
                inputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'secondary.light', fontSize: 14 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}
          {showPartnerSelectFilter ? (
            <Feature name="partnership" is="billing">
              <Grid size={{ xs: 6, sm: 3 }}>
                <PartnersSelect
                  form={{ setFieldValue: handleSearch }}
                  label={i18n.t('common.filter_by_partner')}
                  firstLoad={params.account_emitter_id ? true : false}
                  defaultField={isOwner ? 'id' : 'account_id'}
                  field={{
                    name: isOwner ? 'organization_id' : 'account_emitter_id',
                    value: isOwner ? params.organization_id : params.account_emitter_id || '',
                  }}
                  margin="none"
                />
              </Grid>
            </Feature>
          ) : null}
          {showAccountSelect ? (
            <Grid size={{ xs: 6, sm: 3 }}>
              <AccountStatementSelect
                form={{
                  setFieldValue: handleSearch,
                }}
                margin="none"
                field={{
                  name: 'account_statement_id',
                  value: params.account_statement_id || '',
                }}
                label={i18n.t('account_statement.title')}
                isOwner={isOwner}
              />
            </Grid>
          ) : null}
          <OrgAbility subject={['msServiceOrg']}>
            <Grid size={{ xs: 6, sm: 2 }}>
              <UsersSelect
                type="users"
                margin="none"
                datacy="user-select"
                selector="account_id"
                customParams={{ is_customer: true }}
                label={i18n.t('common.filter_by_customer')}
                firstLoad={params.account_customer_id ? true : false}
                form={{
                  setFieldValue: handleSearch,
                }}
                field={{
                  name: 'account_customer_id',
                  value: params.account_customer_id || '',
                }}
              />
            </Grid>
            <Feature name="partnership" is="billing">
              <Grid size={{ xs: 6, sm: 2 }}>
                <InvoiceTypesSelect
                  form={{
                    setFieldValue: handleSearch,
                  }}
                  margin="none"
                  field={{
                    name: 'type',
                    value: params.type || '',
                  }}
                  label={
                    sharingTypeFFB
                      ? i18n.t('statement_entries.money_flow')
                      : i18n.t('invoice.payment_flow')
                  }
                  disabled={Object.prototype.hasOwnProperty.call(params, 'account_customer_id')}
                  sharingTypeFFB={sharingTypeFFB}
                />
              </Grid>
            </Feature>
          </OrgAbility>
          <Grid size={{ xs: 6, sm: 2 }}>
            <InvoiceStatusesSelect
              form={{
                setFieldValue: handleSearch,
              }}
              margin="none"
              field={{
                name: 'status',
                value: params.status || '',
              }}
              label={i18n.t('common.status')}
            />
          </Grid>
          {/* <Grid item xs={3}>
            <InvoiceSourcesSelect
              form={{
                setFieldValue: handleSearch,
              }}
              type="users"
              margin="none"
              field={{
                name: "source_name",
                value: filters.source_name || "",
              }}
              customParams={
                params.source_name ? { keyword: params.source_name } : {}
              }
              label={i18n.t("invoice.source")}
              selector="account_id"
            />
          </Grid> */}
          {showLatePaymentFilter ? (
            <Grid size={{ xs: 6, sm: 3 }}>
              <FormControlLabel
                value="start"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                control={
                  <Switch
                    onChange={(_, value) => handleSearch('past_due', value)}
                    checked={!!params?.past_due}
                    name="past_due"
                    color="primary"
                  />
                }
                label={i18n.t('invoice.only_late_payments')}
                labelPlacement="start"
              />
            </Grid>
          ) : null}
          <Grid size={12}>
            <Paper elevation={2}>
              <Table
                data={data}
                columns={columns}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                selectableRows={
                  (ability.can('cancel', 'receivable') && user?.role === 'superadmin') ||
                  (ability.can('process', 'receivable') && showSelectCheckBox)
                }
                loading={fetchingInvoices === FETCH_STATE.LOADING}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                clearSelectedRows={clearSelectedRows}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                handleSelectedRowsChange={handleSelected}
              />
            </Paper>
          </Grid>
        </Grid>
        {openRefundModal && (
          <RefundModal
            invoices={selectedInvoices}
            handleClick={handleModalOpen}
            open={openRefundModal}
          />
        )}
        <MarkAsPaid />
      </Container>
    </Layout>
  );
};

export default InvoiceList;
